import { FabricSVGIcons } from '@common/Fabric/icons';
import { FabricProviderType } from '@common/Fabric/types';
import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { Entity } from '@common/types/Entity';
import { VAppProjectEntityKind } from '@prophecy/graphql-types/enums';
import {
  DotsVerticalIcon,
  LayersThreeAIcon,
  MappingIcon,
  OntologyIcon,
  PinBIcon,
  ProjectIcon,
  TrashCIcon
} from '@prophecy/icons';
import { VAppProject } from '@prophecy/queries/common/VerticalApps/projectListQuery';
import { theme } from '@prophecy/theme';
import { Button, Card, Dropdown, Ellipsis, Stack, Tags, Text, Tooltip } from '@prophecy/ui-v3';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { getProjectListingProvider, getProjectListingProviderLabel, isTypesProject } from './entityFilterUtils';

import type { MouseEvent } from 'react';

// Beyond this many total entity tags, the row collapses into per-kind count
// badges (each with a dropdown listing its entities), matching the Figma design.
const MAX_INLINE_ENTITY_TAGS = 2;

type Props = {
  project: VAppProject;
  onClick: (projectId: string) => void;
  /** Omitted where a project kind has no taggable entities to open, e.g. a Types project. */
  onOpenEntity?: (projectId: string, entity: Entity, name: string) => void;
  isPinned: boolean;
  onPinProject: (projectId: string) => void;
  onDeleteProject?: (projectId: string, projectName: string) => void;
};

const StyledCard = styled(Card)`
  cursor: pointer;
  padding: ${theme.spaces.x16};
  width: calc(50% - ${theme.spaces.x8});
  border: 1px solid ${theme.colors.gray200};
  position: relative;

  &:hover {
    border-color: ${theme.colors.brand300};
  }
`;

// Reserve space on the right so the title/tags never sit under the absolute pin + menu buttons.
const HeaderRow = styled(Stack)`
  padding-right: ${theme.spaces.x64};
`;

// Hug the title so entity tags sit immediately to its right, shrinking with an ellipsis only when long.
const ProjectNameWrap = styled.div`
  min-width: 0;
  flex: 0 1 auto;
`;

const DescriptionWrap = styled.div`
  min-height: ${theme.spaces.x20};
`;

const EntityTagLabel = styled(Ellipsis)`
  max-width: 100px;
`;

// `&&` beats the Tags wrapper's `.ui-icon` gray rule on equal specificity without an inline style.
const CDMTagIcon = styled(LayersThreeAIcon)`
  && {
    color: ${theme.colors.pink500};
  }
`;

const DataMappingTagIcon = styled(MappingIcon)`
  && {
    color: ${theme.colors.grayBlue600};
  }
`;

const PinButtonWrap = styled.span<{ $isPinned: boolean }>`
  position: absolute;
  right: ${theme.spaces.x36};
  top: ${theme.spaces.x8};
  opacity: ${(p) => (p.$isPinned ? 1 : 0)};
  transition: opacity 0.15s ease;

  ${StyledCard}:hover & {
    opacity: 1;
  }
`;

const ProviderIconWrap = styled.span`
  display: inline-flex;
  line-height: 0;
`;

const MenuButtonWrap = styled.span`
  position: absolute;
  right: ${theme.spaces.x8};
  top: ${theme.spaces.x8};
  opacity: 0;
  transition: opacity 0.15s ease;

  ${StyledCard}:hover & {
    opacity: 1;
  }
`;

type TaggableEntityConfig = {
  kind: VAppProjectEntityKind;
  entity: Entity;
  /** Plural label used as the collapsed-dropdown header. */
  label: string;
  icon: JSX.Element;
};

type ProjectEntityGroup = {
  config: TaggableEntityConfig;
  names: string[];
  count: number;
};

// Entity kinds shown as clickable tags on the card. Each tag navigates to that
// entity in the project workspace. Other kinds (pipelines, analysis) live in the filter only.
const TAGGABLE_ENTITY_KINDS: TaggableEntityConfig[] = [
  {
    kind: VAppProjectEntityKind.CDM,
    entity: Entity.CDM,
    label: 'CDMs',
    icon: <CDMTagIcon type='default' />
  },
  {
    kind: VAppProjectEntityKind.Mapping,
    entity: Entity.DataMapping,
    label: 'Data Mappings',
    icon: <DataMappingTagIcon type='default' />
  }
];

function getProjectEntityGroups(project: VAppProject): ProjectEntityGroup[] {
  const groups: ProjectEntityGroup[] = [];
  for (const config of TAGGABLE_ENTITY_KINDS) {
    const group = project.entities?.find((projectEntity) => projectEntity.kind === config.kind);
    const names = (group?.items ?? []).map((item) => item.name).filter(Boolean);
    if (names.length > 0) {
      groups.push({ config, names, count: group?.count ?? names.length });
    }
  }
  return groups;
}

function getEntityCount(project: VAppProject, kind: VAppProjectEntityKind): number {
  return project.entities?.find((entity) => entity.kind === kind)?.count ?? 0;
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

// Footer summary listing each entity kind with a non-zero count.
const FOOTER_ENTITY_KINDS: { kind: VAppProjectEntityKind; singular: string; plural: string }[] = [
  { kind: VAppProjectEntityKind.Pipeline, singular: 'Pipeline', plural: 'Pipelines' },
  { kind: VAppProjectEntityKind.CDM, singular: 'CDM', plural: 'CDMs' },
  { kind: VAppProjectEntityKind.Mapping, singular: 'Mapping', plural: 'Mappings' },
  { kind: VAppProjectEntityKind.Analysis, singular: 'Analysis', plural: 'Analyses' }
];

function getProjectFooterMeta(project: VAppProject): string[] {
  const meta: string[] = [];
  // Unlike the entity counts below, zero is shown: a Types project's concept count is what the card is about.
  const conceptCount = project.ontologyProperties?.[0]?.conceptCount;
  if (conceptCount !== undefined) meta.push(pluralize(conceptCount, 'type', 'types'));
  for (const { kind, singular, plural } of FOOTER_ENTITY_KINDS) {
    const count = getEntityCount(project, kind);
    if (count > 0) meta.push(pluralize(count, singular, plural));
  }
  return meta;
}

function EntityCountBadge({
  group,
  onOpenEntity
}: {
  group: ProjectEntityGroup;
  onOpenEntity: (entity: Entity, name: string) => void;
}) {
  const { config, names, count } = group;

  return (
    <Dropdown
      placement='bottom'
      align='start'
      data-test-id={`project-card-entity-badge-${config.entity}`}
      overlay={
        <>
          <Dropdown.Label>{config.label}</Dropdown.Label>
          {names.map((name) => (
            <Dropdown.Item
              key={name}
              data-test-id={`project-card-entity-popover-item-${config.entity}-${name}`}
              onClick={() => onOpenEntity(config.entity, name)}>
              <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                {config.icon}
                <Ellipsis tooltip>
                  <Text level='sm' tone={theme.colors.gray800}>
                    {name}
                  </Text>
                </Ellipsis>
              </Stack>
            </Dropdown.Item>
          ))}
        </>
      }>
      <Tags
        size='s'
        icon={config.icon}
        tooltip={`${count} ${config.label}`}
        data-test-id={`project-card-entity-badge-tag-${config.entity}`}
        onClick={(e: MouseEvent<HTMLButtonElement>) => e.stopPropagation()}>
        {count}
      </Tags>
    </Dropdown>
  );
}

function getProviderIcon(providerType: string | undefined) {
  const provider = getProjectListingProvider(providerType);
  if (provider === ORCHESTRATION_PROVIDER.ProphecyManaged) {
    return FabricSVGIcons[FabricProviderType.Orchestration][provider];
  }
  return provider && FabricSVGIcons[FabricProviderType.SQL][provider];
}

export function ProjectCard({ project, onClick, onOpenEntity, isPinned, onPinProject, onDeleteProject }: Props) {
  const creatorName = project.createdByFullName ?? project.team?.name ?? '';
  const provider = getProjectListingProvider(project.providerType);
  const ProviderIcon = getProviderIcon(project.providerType);
  const entityGroups = getProjectEntityGroups(project);
  const totalTags = entityGroups.reduce((sum, group) => sum + group.names.length, 0);
  const collapsed = totalTags > MAX_INLINE_ENTITY_TAGS;
  const footerMeta = [project.created ? timeAgoFormat(project.created) : '—', ...getProjectFooterMeta(project)].join(
    '  •  '
  );

  return (
    <StyledCard onClick={() => onClick(project._id)}>
      <Stack direction='vertical' gap={theme.spaces.x12}>
        <HeaderRow gap={theme.spaces.x6} direction='horizontal' alignY='center'>
          {isTypesProject(project) ? (
            <OntologyIcon type='extended' color={theme.colors.pink600} />
          ) : (
            <ProjectIcon type='extended' color={theme.colors.grayBlue600} />
          )}
          <ProjectNameWrap>
            <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900} elementType='div'>
              <Ellipsis tooltip tooltipProps={{ title: project.name }}>
                {project.name}
              </Ellipsis>
            </Text>
          </ProjectNameWrap>
          {entityGroups.length > 0 && (
            <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
              {collapsed
                ? entityGroups.map((group) => (
                    <EntityCountBadge
                      key={group.config.kind}
                      group={group}
                      onOpenEntity={(entity, name) => onOpenEntity?.(project._id, entity, name)}
                    />
                  ))
                : entityGroups.flatMap((group) =>
                    group.names.map((name) => (
                      <Tags
                        key={`${group.config.entity}-${name}`}
                        size='s'
                        icon={group.config.icon}
                        data-test-id={`project-card-entity-tag-${group.config.entity}-${name}`}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          onOpenEntity?.(project._id, group.config.entity, name);
                        }}>
                        <EntityTagLabel tooltip>{name}</EntityTagLabel>
                      </Tags>
                    ))
                  )}
            </Stack>
          )}
          <PinButtonWrap $isPinned={isPinned}>
            <Button
              variant='linkGray'
              size='xs'
              aria-label={isPinned ? 'Unpin Project' : 'Pin Project'}
              tooltip={isPinned ? 'Unpin Project' : 'Pin Project'}
              data-test-id='project-card-pin-button'
              icon={<PinBIcon type={isPinned ? 'solid' : 'default'} color={theme.colors.gray600} />}
              onClick={(e) => {
                e.stopPropagation();
                onPinProject(project._id);
              }}
            />
          </PinButtonWrap>
          {onDeleteProject && (
            <MenuButtonWrap>
              <Dropdown
                placement='bottom'
                align='end'
                data-test-id='project-card-menu'
                overlay={
                  <Dropdown.Item
                    data-test-id='project-card-delete-item'
                    onClick={() => onDeleteProject(project._id, project.name)}>
                    <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                      <TrashCIcon type='default' color={theme.colors.gray600} />
                      <Text level='sm' tone={theme.colors.gray600}>
                        Delete project
                      </Text>
                    </Stack>
                  </Dropdown.Item>
                }>
                <Button
                  variant='linkGray'
                  size='xs'
                  tooltip='Project options'
                  aria-label='Project options'
                  data-test-id='project-card-menu-button'
                  icon={<DotsVerticalIcon type='default' color={theme.colors.gray600} />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </MenuButtonWrap>
          )}
        </HeaderRow>

        <DescriptionWrap>
          <Ellipsis tooltip>
            <Text level='sm' tone={theme.colors.gray600}>
              {project.description}
            </Text>
          </Ellipsis>
        </DescriptionWrap>

        <Stack direction='horizontal' align='space-between' alignY='center'>
          <Text level='xs' tone={theme.colors.gray500}>
            {footerMeta}
          </Text>
          {creatorName && (
            <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
              {ProviderIcon && (
                <Tooltip title={provider ? getProjectListingProviderLabel(provider) : undefined}>
                  <ProviderIconWrap>
                    <ProviderIcon name={project.providerType} size='xs' />
                  </ProviderIconWrap>
                </Tooltip>
              )}
              <Text level='xs' tone={theme.colors.gray500}>
                {creatorName}
              </Text>
            </Stack>
          )}
        </Stack>
      </Stack>
    </StyledCard>
  );
}
